import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../modules/auth/AuthContext";

export function LoginPage() {
  const navigate = useNavigate();
  const { signIn, user } = useAuth();

  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [showPassword, setShow]     = useState(false);
  const [rememberMe, setRemember]   = useState(false);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  if (user) {
    return <Navigate to={user.role === "CLIENT" ? "/client" : "/dashboard"} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await signIn(email, password);
      navigate(u.role === "CLIENT" ? "/client" : "/dashboard");
    } catch {
      setError("No se pudo iniciar sesión. Verifica tus credenciales.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="lp-root">
      <div className="lp-card">

        {/* ══════════════ LEFT PANEL ══════════════ */}
        <div className="lp-left">
          {/* Decorative blobs */}
          <span className="lp-blob lp-blob--1" />
          <span className="lp-blob lp-blob--2" />
          <span className="lp-blob lp-blob--3" />

          <div className="lp-left-inner">

            {/* Brand row — logo only */}
            <div className="lp-brand-row">
              <div className="lp-brand-icon">
                <img className="lp-brand-img" src="/logos/jc-logo.png" alt="Jorge Carrillo" />
              </div>
            </div>


            {/* Main copy */}
            <div className="lp-copy">
              <p className="lp-eyebrow">BIENVENIDO</p>
              <h1 className="lp-headline">Inicia sesión</h1>
              <span className="lp-bar" />
              <p className="lp-desc">
                Accede a tu plataforma para gestionar clientes, rutinas,
                progreso y coaching de forma segura y profesional.
              </p>
            </div>

            {/* Feature card */}
            <div className="lp-feature">
              <div className="lp-feature-icon">
                {/* Shield SVG */}
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16 3L5 7.5V15C5 21.1 9.9 26.8 16 28.5C22.1 26.8 27 21.1 27 15V7.5L16 3Z"
                    stroke="rgba(255,255,255,0.9)" strokeWidth="1.8"
                    fill="rgba(255,255,255,0.14)"
                  />
                  <rect x="9" y="15" width="3" height="1.5" rx="0.5" fill="white" />
                  <rect x="20" y="15" width="3" height="1.5" rx="0.5" fill="white" />
                  <rect x="10" y="13.5" width="1.5" height="4.5" rx="0.5" fill="white" />
                  <rect x="20.5" y="13.5" width="1.5" height="4.5" rx="0.5" fill="white" />
                  <rect x="13" y="15.5" width="6" height="1" rx="0.4" fill="white" />
                </svg>
              </div>
              <div className="lp-feature-copy">
                <p className="lp-feature-title">Rendimiento y confianza</p>
                <p className="lp-feature-text">
                  Acceso seguro a tu plataforma y gestión profesional de coaching.
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* ══════════════ RIGHT PANEL ══════════════ */}
        <div className="lp-right">
          <div className="lp-form-box">

            <h2 className="lp-form-title">Iniciar sesión</h2>
            <p className="lp-form-sub">Ingresa tus credenciales para continuar</p>

            <form onSubmit={handleSubmit} className="lp-form" noValidate>

              {/* Email */}
              <div className="lp-field">
                <label htmlFor="lp-email" className="lp-label">Correo electrónico</label>
                <div className="lp-input-wrap">
                  <span className="lp-ico">
                    <svg viewBox="0 0 20 20" fill="none">
                      <rect x="2" y="5" width="16" height="11" rx="2" stroke="#9CA3AF" strokeWidth="1.5"/>
                      <path d="M2 7.5L10 12L18 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </span>
                  <input
                    id="lp-email" type="email" className="lp-input"
                    placeholder="coach@gmail.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    required autoComplete="email"
                  />  
                </div>
              </div>

              {/* Password */}
              <div className="lp-field">
                <label htmlFor="lp-password" className="lp-label">Contraseña</label>
                <div className="lp-input-wrap">
                  <span className="lp-ico">
                    <svg viewBox="0 0 20 20" fill="none">
                      <rect x="4" y="9" width="12" height="9" rx="2" stroke="#9CA3AF" strokeWidth="1.5"/>
                      <path d="M7 9V7C7 5.34 8.34 4 10 4C11.66 4 13 5.34 13 7V9" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="10" cy="13.5" r="1" fill="#9CA3AF"/>
                    </svg>
                  </span>
                  <input
                    id="lp-password" type={showPassword ? "text" : "password"}
                    className="lp-input lp-input--pw"
                    placeholder="••••••••••"
                    value={password} onChange={e => setPassword(e.target.value)}
                    required autoComplete="current-password"
                  />
                  <button type="button" className="lp-eye" onClick={() => setShow(v => !v)}
                    aria-label={showPassword ? "Ocultar" : "Mostrar"}>
                    {showPassword ? (
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M2 10C2 10 5 4 10 4C15 4 18 10 18 10C18 10 15 16 10 16C5 16 2 10 2 10Z" stroke="#9CA3AF" strokeWidth="1.5"/>
                        <circle cx="10" cy="10" r="2.5" stroke="#9CA3AF" strokeWidth="1.5"/>
                        <line x1="3" y1="3" x2="17" y2="17" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M2 10C2 10 5 4 10 4C15 4 18 10 18 10C18 10 15 16 10 16C5 16 2 10 2 10Z" stroke="#9CA3AF" strokeWidth="1.5"/>
                        <circle cx="10" cy="10" r="2.5" stroke="#9CA3AF" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Options row */}
              <div className="lp-options">
                <label className="lp-remember">
                  <input type="checkbox" id="lp-remember" className="lp-check"
                    checked={rememberMe} onChange={e => setRemember(e.target.checked)} />
                  <span>Recordarme</span>
                </label>
                <button type="button" className="lp-forgot">¿Olvidaste tu contraseña?</button>
              </div>

              {/* Error */}
              {error && (
                <div className="lp-error" role="alert">
                  <svg viewBox="0 0 20 20" fill="none" className="lp-error-ico">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 6V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="10" cy="13.5" r="0.75" fill="currentColor"/>
                  </svg>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button type="submit" id="login-submit" className="lp-submit" disabled={loading}>
                {loading ? <span className="lp-spinner" /> : (
                  <>
                    Ingresar
                    <svg viewBox="0 0 20 20" fill="none" className="lp-arrow">
                      <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="lp-divider">
                <span className="lp-line" /><span className="lp-divider-text">¿Aún no tienes una cuenta?</span><span className="lp-line" />
              </div>

              {/* Register */}
              <button type="button" className="lp-register">
                Crea tu cuenta
                <svg viewBox="0 0 20 20" fill="none" className="lp-arrow lp-arrow--sm">
                  <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

            </form>
          </div>
        </div>

      </div>
    </div>
  );
}
